import {Injectable} from '@angular/core';
import {FirebaseService} from '@services/firebase.service';
import {toastPushNotificationConfig} from '@config/toast.config';
import {DispatcherService, ToastService} from '@core/src/public-api';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {UserSyncDevice} from '@models/user.model';
import {NotificationPatient} from '@models/notification.model';
import {BehaviorSubject, Observable} from 'rxjs';
import {configVariableValues} from '@constants/config-variable-values.constants';
import {AppConfigService} from '@services/app-config.service';
import {AppoTurnService} from './appo-turn.service';
import {firebaseTopics} from '@shared/constants/firebase.constants';
import {Router} from '@angular/router';
import {FirebaseMessagePayload} from '@shared/models/firebase.model';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  isNotificationSystemEnabled: boolean;
  isNotificationSystemSupported: boolean;
  isNotificationSystemInitialized = false;
  isReceivingMessages = false;
  private _hasNotificationPending = new BehaviorSubject(false);

  constructor(
    private firebaseService: FirebaseService,
    private toastService: ToastService,
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private appoTurnService: AppoTurnService,
    private dispatcher: DispatcherService,
    private router: Router
  ) {
    this.isNotificationSystemEnabled = this.appConfigService.hasConfigurationsEnabled([configVariableValues.PUSH_NOTIFICATION]);
    this.isNotificationSystemSupported = this.isNotificationApiSupported();
  }

  isNotificationApiSupported(): boolean {
    return 'Notification' in window && this.firebaseService.isNotificationApiSupported();
  }

  getNotificationPendingObservable(): Observable<boolean> {
    return this._hasNotificationPending.asObservable();
  }

  setNotificationPending(hasNotificationPending: boolean): void {
    this._hasNotificationPending.next(hasNotificationPending);
  }

  async resetNotificationPending(): Promise<void> {
    const hasNotificationPending = await this.getHasNotificationsPendings();
    this.setNotificationPending(hasNotificationPending);
  }

  initNotificationSystem(): void {
    if (this.isNotificationSystemInitialized || !this.isNotificationSystemEnabled) {
      return;
    }

    if (!this.isNotificationSystemSupported) {
      console.log('Notification API not supported in this browser');
      return;
    }

    this.isNotificationSystemInitialized = true;
    this.firebaseService.initFirebase();
    this.receiveMessages();
  }

  async requestPermissionProcess(): Promise<void> {
    if (!this.isNotificationSystemEnabled) {
      return;
    }

    this.initNotificationSystem();
    if (!this.isNotificationSystemSupported) {
      return;
    }

    const token = await this.requestPermission();
    const model = {token} as UserSyncDevice;
    await this.syncUserDevice(model);
  }

  private async requestPermission(): Promise<string> {
    this.initNotificationSystem();
    console.log('Requesting permission...');
    return new Promise(async (resolve, reject) => {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        console.log('Notification permission granted.');
        const tokenFirebase = await this.firebaseService.getToken();
        resolve(tokenFirebase);
      } else {
        reject(new Error('Notification permission rejected.'));
      }
    });
  }

  private receiveMessages(): void {
    if (this.isReceivingMessages) {
      return;
    }

    this.isReceivingMessages = true;
    this.firebaseService.receiveMessage().subscribe(message => {
      if (!message?.notification?.title && !message?.notification?.body) {
        return;
      }

      this.setNotificationPending(true);
      switch (message?.data?.topic) {
        case firebaseTopics.MY_TURN:
          this.appoTurnService.openDialogTurn(message.data);
          this.dispatcher.exec('pasi-received-notification-my-turn');
          break;
        default:
          this.showToastClickRedirect(message);
      }
    });
  }

  async showToastClickRedirect(message: FirebaseMessagePayload): Promise<void> {
    const toastRef = await this.toastService.show(message.notification?.body, message.notification?.title, toastPushNotificationConfig);
    if (message.data?.link) {
      toastRef.onTap.subscribe(() => {
        this.router.navigateByUrl(message.data?.link);
      });
    }
  }

  private syncUserDevice(model: UserSyncDevice): Promise<any> {
    const url = '/api/v1/user/device';
    return this.http.put(url, model).toPromise();
  }

  arePushNotificationsGranted(): boolean {
    if (!this.isNotificationSystemSupported) {
      return false;
    }

    return Notification?.permission === 'granted';
  }

  getHasNotificationsPendings(): Promise<boolean> {
    const url = `/api/v1/notification/pending`;
    return this.http.get<boolean>(url).toPromise();
  }

  getNotifications(page: number, size: number, read?: boolean): Promise<HttpResponse<NotificationPatient[]>> {
    const url = `/api/v1/notification`;
    let httpParams = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (read != null) {
      httpParams = httpParams.set('read', read.toString());
    }

    return this.http.get<NotificationPatient[]>(url, {params: httpParams, observe: 'response'}).toPromise();
  }

  deleteAllNotifications(): Promise<void> {
    const url = `/api/v1/notification`;
    return this.http.delete<void>(url).toPromise();
  }

  markNotificationRead(notificationId: number): Promise<void> {
    const url = `/api/v1/notification/${notificationId}`;
    return this.http.put<void>(url, {}).toPromise();
  }

  markAllRead(): Promise<void> {
    const url = `/api/v1/notification`;
    return this.http.put<void>(url, {}).toPromise();
  }
}
